import React, { useState } from 'react';
import { Button, Card, Typography, App } from 'antd';
import { CopyOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { BaseCardModel, CommonEditComponentProps } from '../../dto/model';

const { Title } = Typography;

type CommonCardProps<T extends BaseCardModel> = {
  model: T;
  onSubmit: (form: T) => void;
  onDelete: (form: T) => void;
  onDuplicate: (model: T) => void;
  EditComponent: React.ComponentType<CommonEditComponentProps<T>>;
};

export const CommonCard = <T extends BaseCardModel>({ model, onSubmit, onDelete, onDuplicate, EditComponent }: CommonCardProps<T>) => {
  const { modal } = App.useApp();
  const [shouldEdit, setShouldEdit] = useState(false);
  const [currentModel] = useState<T>(model);

  const setEditDisplay = (status: boolean) => {
    setShouldEdit(status);
  };

  const deleteCard = () => {
    onDelete(model);
  };

  const showDeleteConfirm = () => {
    modal.confirm({
      title: 'Are you sure you want to delete this?',
      icon: <DeleteOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        deleteCard();
      },
    });
  };

  const handleDuplicate = () => {
    const duplicated = { ...model };
    onDuplicate(duplicated);
  };

  return (
    <Card size="small" style={{ marginBottom: '12px' }}>
      {shouldEdit ? (
        <div>
          <EditComponent model={currentModel} onSubmit={onSubmit} onCancel={() => setEditDisplay(false)} />
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Title level={4} style={{ margin: '0 0 4px' }}>
              {currentModel.name}
            </Title>
            <Typography.Text type="secondary">{currentModel.description}</Typography.Text>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setEditDisplay(true)} icon={<EditOutlined />}>
              Edit
            </Button>
            <Button onClick={handleDuplicate} icon={<CopyOutlined />}>
              Duplicate
            </Button>
            <Button danger onClick={showDeleteConfirm} icon={<DeleteOutlined />}>
              Delete
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};
